import React, { useEffect, useState } from 'react';
import {
  Form,
  Label,
  Pagination,
  Popup,
  Select,
  Table,
} from 'semantic-ui-react';
import { API, isAdmin, showError, timestamp2string } from '../helpers';
import { ITEMS_PER_PAGE } from '../constants';
import { renderQuota } from '../helpers/render';
import { useTranslation } from 'react-i18next';

const CACHE_OPTIONS = [
  { key: 'all', text: '全部', value: '' },
  { key: 'hit', text: '命中缓存', value: 'true' },
  { key: 'miss', text: '未命中', value: 'false' },
];

function previewText(text) {
  if (!text) {
    return '-';
  }
  if (text.length > 80) {
    return text.slice(0, 80) + '...';
  }
  return text;
}

const ApiRequestsTable = () => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [requestUrl, setRequestUrl] = useState('');
  const [cacheHit, setCacheHit] = useState('');
  const admin = isAdmin();

  const loadLogs = async (startIdx) => {
    let url = admin
      ? `/api/api_request/?p=${startIdx}`
      : `/api/api_request/self?p=${startIdx}`;
    if (requestUrl) {
      url += `&request_url=${encodeURIComponent(requestUrl)}`;
    }
    if (cacheHit) {
      url += `&cache_hit=${cacheHit}`;
    }
    const res = await API.get(url);
    const { success, message, data } = res.data;
    if (success) {
      if (startIdx === 0) {
        setLogs(data);
      } else {
        setLogs((prev) => [...prev, ...data]);
      }
    } else {
      showError(message);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadLogs(0).catch((reason) => showError(reason));
  }, []);

  const onPaginationChange = (e, { activePage }) => {
    (async () => {
      if (activePage === Math.ceil(logs.length / ITEMS_PER_PAGE) + 1) {
        await loadLogs(activePage - 1);
      }
      setActivePage(activePage);
    })();
  };

  const searchLogs = async () => {
    setLoading(true);
    setActivePage(1);
    await loadLogs(0);
  };

  return (
    <>
      <Form onSubmit={searchLogs}>
        <Form.Group>
          <Form.Input
            icon='search'
            iconPosition='left'
            placeholder='请求地址'
            value={requestUrl}
            onChange={(e, { value }) => setRequestUrl(value.trim())}
            width={8}
          />
          <Form.Field width={4}>
            <Select
              options={CACHE_OPTIONS}
              value={cacheHit}
              onChange={(e, { value }) => setCacheHit(value)}
            />
          </Form.Field>
          <Form.Button loading={loading}>查询</Form.Button>
        </Form.Group>
      </Form>
      <Table basic={'very'} compact size='small'>
        <Table.Header>
          <Table.Row>
            {admin && <Table.HeaderCell>用户</Table.HeaderCell>}
            <Table.HeaderCell>时间</Table.HeaderCell>
            <Table.HeaderCell>方法</Table.HeaderCell>
            <Table.HeaderCell>请求地址</Table.HeaderCell>
            <Table.HeaderCell>请求参数</Table.HeaderCell>
            <Table.HeaderCell>返回参数</Table.HeaderCell>
            <Table.HeaderCell>缓存</Table.HeaderCell>
            <Table.HeaderCell>状态</Table.HeaderCell>
            <Table.HeaderCell>额度</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {logs
            .slice((activePage - 1) * ITEMS_PER_PAGE, activePage * ITEMS_PER_PAGE)
            .map((log) => (
              <Table.Row key={log.id}>
                {admin && <Table.Cell>{log.user_id}</Table.Cell>}
                <Table.Cell>{timestamp2string(log.created_at)}</Table.Cell>
                <Table.Cell>{log.request_method}</Table.Cell>
                <Table.Cell>{log.request_url}</Table.Cell>
                <Table.Cell>
                  <Popup
                    content={<pre style={{ maxWidth: 480 }}>{log.request_params || '-'}</pre>}
                    trigger={<span>{previewText(log.request_params)}</span>}
                    hoverable
                  />
                </Table.Cell>
                <Table.Cell>
                  <Popup
                    content={<pre style={{ maxWidth: 480 }}>{log.response_params || '-'}</pre>}
                    trigger={<span>{previewText(log.response_params)}</span>}
                    hoverable
                  />
                </Table.Cell>
                <Table.Cell>
                  {log.cache_hit ? (
                    <Label basic color='green'>
                      命中
                    </Label>
                  ) : (
                    <Label basic>未命中</Label>
                  )}
                </Table.Cell>
                <Table.Cell>{log.status_code}</Table.Cell>
                <Table.Cell>{renderQuota(log.quota, t)}</Table.Cell>
              </Table.Row>
            ))}
        </Table.Body>
        <Table.Footer>
          <Table.Row>
            <Table.HeaderCell colSpan={admin ? '9' : '8'}>
              <Pagination
                floated='right'
                activePage={activePage}
                onPageChange={onPaginationChange}
                size='small'
                siblingRange={1}
                totalPages={
                  Math.ceil(logs.length / ITEMS_PER_PAGE) +
                  (logs.length % ITEMS_PER_PAGE === 0 ? 1 : 0)
                }
              />
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      </Table>
    </>
  );
};

export default ApiRequestsTable;
